import CharacterCount from '@boViews/shared/CharacterCount';
import CollapsibleSection from '@boViews/shared/CollapsibleSection';
import LanguageTabs from '@boViews/shared/LanguageTabs';
import { languageName, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import { DURATION_MAX_MINUTES, durationRules } from '@boViews/shared/testDuration';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import FormGroup from '@sharedComponents/FormGroup';
import Grid from '@sharedComponents/Grid';
import InputText from '@sharedComponents/InputText';
import InputTextArea from '@sharedComponents/InputTextArea';
import Select from '@sharedComponents/Select';
import ToggleSwitch from '@sharedComponents/ToggleSwitch';
import Typography from '@sharedComponents/Typography';
import { REFERENCE_LANGUAGE } from '@src/i18n';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DESCRIPTION_MAX_LENGTH, emptyLanguages, TITLE_MAX_LENGTH } from '../testForm';

import * as style from './style.module.css';

/**
 * "Information" section of the test page: its read-only reference, its title and description in the active language,
 * then its language, level, duration in minutes, pedagogical flag and status. It reads and writes the page form
 * (react-hook-form context); `onSave` submits it. `languages` are the catalogue languages (their id labelled by
 * their code) and `levels` the levels of the tests.
 */
const InformationSection = ({
    reference = '',
    languages = [],
    levels = [],
    activeLanguage = REFERENCE_LANGUAGE,
    saving = false,
    onLanguageChange = () => {},
    onSave = () => {},
}) => {
    const { t, i18n } = useTranslation('bo');
    const locale = uiLocale(i18n);
    const { register, control, clearErrors } = useFormContext();
    const { errors, isDirty } = useFormState({ control });
    const [titles, descriptions] = useWatch({ control, name: ['title', 'description'] });
    const title = titles?.[activeLanguage] ?? '';
    const description = descriptions?.[activeLanguage] ?? '';
    const titleError = errors.title?.[activeLanguage]?.message;
    const descriptionError = errors.description?.[activeLanguage]?.message;
    const durationError = errors.duration?.message;
    const languageChoices = languages.map((option) => ({ ...option, label: languageName(option.label, locale) }));
    const statusChoices = [
        { value: 'DRAFT', label: t('Draft') },
        { value: 'ONLINE', label: t('Online') },
        { value: 'ARCHIVED', label: t('Archived') },
    ];

    return (
        <CollapsibleSection title={t('Information')}>
            <FormGroup label={t('Reference')}>
                <InputText aria-label={t('Reference')} value={reference} readOnly disabled />
            </FormGroup>
            <FlexBox flexDirection="column" className={style.languageBox}>
                <LanguageTabs
                    languages={languages}
                    active={activeLanguage}
                    emptyLanguages={emptyLanguages(titles, languages)}
                    referenceLanguage={REFERENCE_LANGUAGE}
                    onChange={onLanguageChange}
                />
                <FlexBox flexDirection="column" gap="xs" className={style.fields}>
                    <FormGroup label={t('Title')} errorMsg={titleError}>
                        <InputText
                            key={`title-${activeLanguage}`}
                            aria-label={t('Title')}
                            isError={Boolean(titleError)}
                            {...register(`title.${activeLanguage}`, { onChange: () => clearErrors(`title.${activeLanguage}`) })}
                        />
                        <CharacterCount length={title.length} max={TITLE_MAX_LENGTH} />
                    </FormGroup>
                    <FormGroup label={t('Description')} errorMsg={descriptionError}>
                        <InputTextArea
                            key={`description-${activeLanguage}`}
                            aria-label={t('Description')}
                            isError={Boolean(descriptionError)}
                            {...register(`description.${activeLanguage}`, { onChange: () => clearErrors(`description.${activeLanguage}`) })}
                        />
                        <CharacterCount length={description.length} max={DESCRIPTION_MAX_LENGTH} />
                    </FormGroup>
                </FlexBox>
            </FlexBox>
            <Grid>
                <FormGroup label={t('Language')}>
                    <Select
                        control={control}
                        register={{ name: 'languageId' }}
                        options={languageChoices}
                        isSearchable={false}
                        aria-label={t('Language')}
                    />
                </FormGroup>
                <FormGroup label={t('Level')}>
                    <Select
                        control={control}
                        register={{ name: 'levelId' }}
                        options={levels}
                        isSearchable={false}
                        aria-label={t('Level')}
                    />
                </FormGroup>
                <FormGroup label={t('Duration (minutes)')} errorMsg={durationError}>
                    {/* A text input: a number input would turn letters into an empty value, saved as no duration. */}
                    <InputText
                        inputMode="numeric"
                        aria-label={t('Duration (minutes)')}
                        isError={Boolean(durationError)}
                        {...register('duration', {
                            ...durationRules(
                                t('The duration must be a whole number of minutes between 1 and {{max}}', { max: DURATION_MAX_MINUTES }),
                            ),
                            onChange: () => clearErrors('duration'),
                        })}
                    />
                </FormGroup>
                <FormGroup label={t('Status')}>
                    <Select
                        control={control}
                        register={{ name: 'status' }}
                        options={statusChoices}
                        isSearchable={false}
                        aria-label={t('Status')}
                    />
                </FormGroup>
            </Grid>
            <FlexBox alignItems="center" gap="s">
                <ToggleSwitch register={register('pedagogical')} ariaLabel={t('Pedagogical')} />
                <Typography>{t('Pedagogical')}</Typography>
            </FlexBox>
            <FlexBox justifyContent="flex-end">
                <Button label={t('Save modifications')} action={onSave} disabled={!isDirty || saving} isLoading={saving} />
            </FlexBox>
        </CollapsibleSection>
    );
};

export default InformationSection;
